// Modell des Filter-Editors (Agent 5A, Runde 5), rein und ohne React (mit `node --test` prüfbar).
//
// Eine Abfrage (`s_query`) hat die Form von Close: {query: Baum, sort: [...], results_limit}. Dieses Modul liest den Baum in ein
// Modell, das der Editor bearbeitet (Gruppen, Bedingungen, verwandte Objekte), und schreibt es wieder zurück. Es erhält, was es nicht
// versteht: unbekannte Knoten und Bedingungen bleiben als „roh“ stehen und werden unverändert zurückgeschrieben, nie still verändert.
//
// Aufbau des Baums wie Close ihn speichert: `and[object_type, …Bedingungen]` (flach) oder `and[object_type, Gruppe]` (eine Gruppe darunter).
// Das Modell merkt sich, welche Form es gelesen hat (`flach`), damit eine unveränderte Abfrage Zeichen für Zeichen dieselbe bleibt.
//
// Unvollständige Bedingungen (noch kein Wert gewählt) zählen im Editor mit, gehen aber nicht in die Abfrage: sonst würde ein eben
// hinzugefügter Filter ohne Wert die Liste leeren.
//
// Die Datei hat keine Laufzeit-Importe (nur `import type`), damit `node --test` sie ohne Bündelung lädt.
import type { FilterFeldRef, FilterKatalog, FilterKatalogFeld } from "../../api/filter";

// ---------------------------------------------------------------------------------------------------------------------------------
// Typen
// ---------------------------------------------------------------------------------------------------------------------------------

export type Einheit = "minutes" | "hours" | "days" | "weeks" | "months" | "years";
export type TextModus = "beginning_of_words" | "phrase" | "full_words";
export type Tagesgrenze = "start" | "end" | null;

export type Moment =
  | { art: "jetzt" }
  | { art: "versatz"; menge: number; einheit: Einheit; richtung: "past" | "future"; tagesgrenze: Tagesgrenze }
  | { art: "periode"; bereich: string; which: "start" | "end" }
  | { art: "datum"; tag: string; which: "start" | "end" }
  | { art: "roh"; roh: unknown };

export interface Grenzen {
  gt: number | null;
  gte: number | null;
  lt: number | null;
  lte: number | null;
}

export type Bedingung =
  | { art: "verweis"; typ: string; ids: string[] }
  | { art: "begriff"; werte: string[] }
  | { art: "zeitraum"; ab: Moment | null; vor: Moment | null }
  | ({ art: "zahl" } & Grenzen)
  | ({ art: "dauer" } & Grenzen)
  | { art: "text"; modus: TextModus; wert: string }
  | { art: "existiert" }
  | { art: "roh"; roh: unknown };

export interface Gruppe {
  typ: "gruppe";
  id: string;
  und: boolean;
  negate: boolean;
  kinder: Knoten[];
}
export interface FeldKnoten {
  typ: "feld";
  id: string;
  feld: FilterFeldRef;
  negate: boolean;
  bedingung: Bedingung;
}
export interface VerwandtKnoten {
  typ: "verwandt";
  id: string;
  dieses: string;
  verwandt: string;
  negate: boolean;
  innen: Gruppe;
}
export interface VolltextKnoten {
  typ: "volltext";
  id: string;
  negate: boolean;
  modus: TextModus;
  wert: string;
}
export interface RohKnoten {
  typ: "roh";
  id: string;
  roh: unknown;
}
export type Knoten = Gruppe | FeldKnoten | VerwandtKnoten | VolltextKnoten | RohKnoten;

export interface Abfragemodell {
  objekt: string;
  wurzel: Gruppe;
  /** gelesen als `and[object_type, Bedingung, Bedingung …]` (true) oder `and[object_type, Gruppe]` (false) */
  flach: boolean;
  sort: unknown[];
  resultsLimit: number | null;
}

export interface SQuery {
  query?: Record<string, unknown> | null;
  sort?: unknown[] | null;
  results_limit?: number | null;
}

type Obj = Record<string, unknown>;
const istObj = (w: unknown): w is Obj => typeof w === "object" && w !== null && !Array.isArray(w);

let zaehler = 0;
export const neueId = (): string => `k${++zaehler}`;

// ---------------------------------------------------------------------------------------------------------------------------------
// Lesen
// ---------------------------------------------------------------------------------------------------------------------------------

const EINHEITEN: Einheit[] = ["years", "months", "weeks", "days", "hours", "minutes"];
const TEXT_MODI: TextModus[] = ["beginning_of_words", "phrase", "full_words"];

function momentLesen(roh: unknown): Moment | null {
  if (roh === null || roh === undefined) return null;
  if (!istObj(roh)) return { art: "roh", roh };
  switch (roh.type) {
    case "now":
      return { art: "jetzt" };
    case "offset": {
      const basis = istObj(roh.moment) ? roh.moment : { type: "now" };
      const versatz = istObj(roh.offset) ? roh.offset : {};
      const richtung = roh.direction;
      const grenze = roh.which_day_end ?? null;
      if (basis.type !== "now" || (richtung !== "past" && richtung !== "future") || (grenze !== null && grenze !== "start" && grenze !== "end")) return { art: "roh", roh };
      if (Number(versatz.seconds ?? 0) !== 0) return { art: "roh", roh };
      const belegt = EINHEITEN.filter((e) => Number(versatz[e] ?? 0) !== 0);
      if (belegt.length > 1 || Object.keys(versatz).some((k) => k !== "seconds" && !EINHEITEN.includes(k as Einheit))) return { art: "roh", roh };
      const einheit = belegt[0] ?? "days";
      const menge = Number(versatz[einheit] ?? 0);
      if (!Number.isFinite(menge)) return { art: "roh", roh };
      return { art: "versatz", menge, einheit, richtung, tagesgrenze: grenze };
    }
    case "start_end_of_predefined_relative_period": {
      const which = roh.which ?? "start";
      if (typeof roh.range !== "string" || (which !== "start" && which !== "end")) return { art: "roh", roh };
      return { art: "periode", bereich: roh.range, which };
    }
    case "fixed_local_date": {
      const which = roh.which ?? "start";
      if (typeof roh.value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(roh.value) || (which !== "start" && which !== "end")) return { art: "roh", roh };
      return { art: "datum", tag: roh.value, which };
    }
    default:
      return { art: "roh", roh };
  }
}

function momentSchreiben(m: Moment): unknown {
  switch (m.art) {
    case "jetzt":
      return { type: "now" };
    case "versatz": {
      const offset: Record<string, number> = { years: 0, months: 0, weeks: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
      offset[m.einheit] = m.menge;
      return { type: "offset", moment: { type: "now" }, offset, direction: m.richtung, which_day_end: m.tagesgrenze };
    }
    case "periode":
      return { type: "start_end_of_predefined_relative_period", range: m.bereich, which: m.which };
    case "datum":
      return { type: "fixed_local_date", value: m.tag, which: m.which };
    default:
      return m.roh;
  }
}

function zahlOderNull(w: unknown): number | null | "kaputt" {
  if (w === null || w === undefined) return null;
  return typeof w === "number" && Number.isFinite(w) ? w : "kaputt";
}

function dauerSekunden(w: unknown): number | null | "kaputt" {
  if (w === null || w === undefined) return null;
  if (typeof w === "number") return Number.isFinite(w) ? w : "kaputt";
  if (!istObj(w)) return "kaputt";
  if (Number(w.years ?? 0) !== 0 || Number(w.months ?? 0) !== 0) return "kaputt";
  const faktoren: Record<string, number> = { weeks: 604800, days: 86400, hours: 3600, minutes: 60, seconds: 1 };
  let summe = 0;
  for (const [k, v] of Object.entries(w)) {
    if (k === "years" || k === "months") continue;
    if (!(k in faktoren) || typeof v !== "number") return "kaputt";
    summe += v * faktoren[k];
  }
  return summe;
}

function bedingungLesen(roh: unknown): Bedingung {
  if (!istObj(roh)) return { art: "roh", roh };
  switch (roh.type) {
    case "reference":
      if (Array.isArray(roh.object_ids) && roh.object_ids.every((i) => typeof i === "string") && typeof roh.reference_type === "string")
        return { art: "verweis", typ: roh.reference_type, ids: roh.object_ids as string[] };
      return { art: "roh", roh };
    case "moment_range": {
      const ab = momentLesen(roh.on_or_after);
      const vor = momentLesen(roh.before);
      return { art: "zeitraum", ab, vor };
    }
    case "number_range":
    case "duration_range": {
      const lesen = roh.type === "number_range" ? zahlOderNull : dauerSekunden;
      const g = { gt: lesen(roh.gt), gte: lesen(roh.gte), lt: lesen(roh.lt), lte: lesen(roh.lte) };
      if (Object.values(g).includes("kaputt")) return { art: "roh", roh };
      return { art: roh.type === "number_range" ? "zahl" : "dauer", ...(g as Grenzen) };
    }
    case "term":
      if (Array.isArray(roh.values) && roh.values.every((v) => typeof v === "string" || typeof v === "number")) return { art: "begriff", werte: roh.values.map(String) };
      return { art: "roh", roh };
    case "exists":
      return { art: "existiert" };
    case "text": {
      const modus = (roh.mode ?? "beginning_of_words") as TextModus;
      if (!TEXT_MODI.includes(modus) || typeof (roh.value ?? "") !== "string") return { art: "roh", roh };
      return { art: "text", modus, wert: String(roh.value ?? "") };
    }
    default:
      return { art: "roh", roh };
  }
}

function dauerSchreiben(sek: number): unknown {
  const t = Math.floor(sek / 86400);
  const h = Math.floor((sek % 86400) / 3600);
  const m = Math.floor((sek % 3600) / 60);
  return { years: 0, months: 0, weeks: 0, days: t, hours: h, minutes: m, seconds: sek % 60 };
}

function bedingungSchreiben(b: Bedingung): unknown {
  switch (b.art) {
    case "verweis":
      return { type: "reference", reference_type: b.typ, object_ids: b.ids };
    case "begriff":
      return { type: "term", values: b.werte };
    case "zeitraum":
      return { type: "moment_range", on_or_after: b.ab ? momentSchreiben(b.ab) : null, before: b.vor ? momentSchreiben(b.vor) : null };
    case "zahl":
    case "dauer": {
      const aus: Obj = { type: b.art === "zahl" ? "number_range" : "duration_range" };
      for (const k of ["gt", "gte", "lt", "lte"] as const) {
        const w = b[k];
        if (w !== null) aus[k] = b.art === "dauer" ? dauerSchreiben(w) : w;
      }
      return aus;
    }
    case "text":
      return { type: "text", mode: b.modus, value: b.wert };
    case "existiert":
      return { type: "exists" };
    default:
      return b.roh;
  }
}

function feldRefLesen(roh: unknown): FilterFeldRef | null {
  if (!istObj(roh)) return null;
  if (roh.type === "regular_field" && typeof roh.field_name === "string" && roh.field_name)
    return { type: "regular_field", object_type: typeof roh.object_type === "string" ? roh.object_type : "lead", field_name: roh.field_name };
  if (roh.type === "custom_field" && typeof roh.custom_field_id === "string" && roh.custom_field_id) return { type: "custom_field", custom_field_id: roh.custom_field_id };
  return null;
}

function gruppeLesen(roh: Obj): Gruppe {
  const kinder = Array.isArray(roh.queries) ? roh.queries.map(knotenLesen) : [];
  return { typ: "gruppe", id: neueId(), und: roh.type === "and", negate: roh.negate === true, kinder };
}

const istGruppe = (roh: unknown): boolean => istObj(roh) && (roh.type === "and" || roh.type === "or") && Array.isArray(roh.queries);

function knotenLesen(roh: unknown): Knoten {
  if (!istObj(roh) || (roh.negate !== undefined && typeof roh.negate !== "boolean")) return { typ: "roh", id: neueId(), roh };
  const negate = roh.negate === true;
  if (istGruppe(roh)) return gruppeLesen(roh);
  if (roh.type === "field_condition") {
    const feld = feldRefLesen(roh.field);
    if (!feld) return { typ: "roh", id: neueId(), roh };
    return { typ: "feld", id: neueId(), feld, negate, bedingung: bedingungLesen(roh.condition) };
  }
  if (roh.type === "has_related" && typeof roh.this_object_type === "string" && typeof roh.related_object_type === "string") {
    const innen = istGruppe(roh.related_query)
      ? gruppeLesen(roh.related_query as Obj)
      : ({ typ: "gruppe", id: neueId(), und: true, negate: false, kinder: roh.related_query ? [knotenLesen(roh.related_query)] : [] } as Gruppe);
    return { typ: "verwandt", id: neueId(), dieses: roh.this_object_type, verwandt: roh.related_object_type, negate, innen };
  }
  if (roh.type === "text" && typeof (roh.value ?? "") === "string" && TEXT_MODI.includes((roh.mode ?? "full_words") as TextModus))
    return { typ: "volltext", id: neueId(), negate, modus: (roh.mode ?? "full_words") as TextModus, wert: String(roh.value ?? "") };
  return { typ: "roh", id: neueId(), roh };
}

export function leereGruppe(und = true): Gruppe {
  return { typ: "gruppe", id: neueId(), und, negate: false, kinder: [] };
}

/** `s_query` einer Smart View lesen. `objektHinweis`: Objektart, falls der Baum keinen `object_type`-Knoten hat. */
export function lesen(sQuery: SQuery | null | undefined, objektHinweis = "lead"): Abfragemodell {
  const sort = Array.isArray(sQuery?.sort) ? [...(sQuery?.sort as unknown[])] : [];
  const resultsLimit = typeof sQuery?.results_limit === "number" ? sQuery.results_limit : null;
  const roh = sQuery?.query;
  if (!istObj(roh)) return { objekt: objektHinweis, wurzel: leereGruppe(), flach: true, sort, resultsLimit };
  if (roh.type === "and" && roh.negate !== true && Array.isArray(roh.queries)) {
    const i = roh.queries.findIndex((k) => istObj(k) && k.type === "object_type" && k.negate !== true && typeof k.object_type === "string");
    if (i >= 0) {
      const objekt = String((roh.queries[i] as Obj).object_type);
      const rest = roh.queries.filter((_, j) => j !== i);
      if (rest.length === 1 && istGruppe(rest[0])) return { objekt, wurzel: gruppeLesen(rest[0] as Obj), flach: false, sort, resultsLimit };
      return { objekt, wurzel: { typ: "gruppe", id: neueId(), und: true, negate: false, kinder: rest.map(knotenLesen) }, flach: true, sort, resultsLimit };
    }
  }
  const wurzel = istGruppe(roh) ? gruppeLesen(roh) : { typ: "gruppe" as const, id: neueId(), und: true, negate: false, kinder: [knotenLesen(roh)] };
  return { objekt: objektHinweis, wurzel, flach: false, sort, resultsLimit };
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Schreiben
// ---------------------------------------------------------------------------------------------------------------------------------

/** Ist die Bedingung so weit ausgefüllt, dass sie in die Abfrage darf? */
export function istVollstaendig(k: Knoten): boolean {
  if (k.typ === "roh") return true;
  if (k.typ === "volltext") return k.wert.trim() !== "";
  if (k.typ === "gruppe") return true;
  if (k.typ === "verwandt") return true;
  const b = k.bedingung;
  switch (b.art) {
    case "verweis":
      return b.ids.length > 0;
    case "begriff":
      return b.werte.length > 0;
    case "zeitraum":
      return b.ab !== null || b.vor !== null;
    case "zahl":
    case "dauer":
      return b.gt !== null || b.gte !== null || b.lt !== null || b.lte !== null;
    case "text":
      return b.wert.trim() !== "";
    default:
      return true;
  }
}

function knotenSchreiben(k: Knoten, wurzel = false): unknown {
  switch (k.typ) {
    case "roh":
      return k.roh;
    case "feld":
      return istVollstaendig(k) ? { type: "field_condition", field: k.feld, condition: bedingungSchreiben(k.bedingung), negate: k.negate } : null;
    case "volltext":
      return istVollstaendig(k) ? { type: "text", mode: k.modus, value: k.wert, negate: k.negate } : null;
    case "verwandt": {
      const innen = gruppeSchreiben(k.innen, true) ?? { type: "and", negate: false, queries: [] };
      return { type: "has_related", this_object_type: k.dieses, related_object_type: k.verwandt, related_query: innen, negate: k.negate };
    }
    case "gruppe":
      return gruppeSchreiben(k, wurzel);
  }
}

/** Leere Gruppen fallen weg (eine leere „oder“-Gruppe träfe nichts), außer der Wurzel und dem Innern eines verwandten Objekts. */
function gruppeSchreiben(g: Gruppe, behalten: boolean): unknown {
  const kinder = g.kinder.map((k) => knotenSchreiben(k)).filter((k) => k !== null);
  if (!kinder.length && !behalten) return null;
  return { type: g.und ? "and" : "or", negate: g.negate, queries: kinder };
}

/** Modell zurück in die Form von Close. */
export function schreiben(m: Abfragemodell): SQuery & { query: Record<string, unknown>; sort: unknown[]; results_limit: number | null } {
  const objekt = { type: "object_type", object_type: m.objekt, negate: false };
  let query: Record<string, unknown>;
  if (m.flach && m.wurzel.und && !m.wurzel.negate) {
    const kinder = m.wurzel.kinder.map((k) => knotenSchreiben(k)).filter((k) => k !== null);
    query = { type: "and", negate: false, queries: [objekt, ...kinder] };
  } else {
    const g = gruppeSchreiben(m.wurzel, false);
    query = { type: "and", negate: false, queries: g ? [objekt, g] : [objekt] };
  }
  return { query, sort: m.sort, results_limit: m.resultsLimit };
}

/** JSON mit sortierten Schlüsseln: zwei gleiche Abfragen ergeben denselben Text, egal in welcher Reihenfolge die Schlüssel stehen. */
export function kanonisch(wert: unknown): string {
  const ordnen = (w: unknown): unknown => {
    if (Array.isArray(w)) return w.map(ordnen);
    if (istObj(w)) return Object.fromEntries(Object.keys(w).sort().map((k) => [k, ordnen(w[k])]));
    return w;
  };
  return JSON.stringify(ordnen(wert));
}

/** Abfrage in die Form bringen, die das Modell schreibt (zum Vergleichen von gespeichert und bearbeitet). */
export function normalisiert(sQuery: SQuery | null | undefined, objektHinweis = "lead"): SQuery {
  return schreiben(lesen(sQuery, objektHinweis));
}

export function gleicheAbfrage(a: SQuery | null | undefined, b: SQuery | null | undefined, objektHinweis = "lead"): boolean {
  return kanonisch(normalisiert(a, objektHinweis)) === kanonisch(normalisiert(b, objektHinweis));
}

/** Abfrage „alle Objekte“ mit der Standardsortierung der Leads-Liste (zuletzt geändert zuerst). */
export function standardAbfrage(objekt = "lead", sortFeld: string | null = "date_updated"): SQuery {
  const m: Abfragemodell = { objekt, wurzel: leereGruppe(), flach: true, sort: [], resultsLimit: null };
  if (sortFeld) m.sort = [{ direction: "desc", field: { type: "regular_field", object_type: objekt, field_name: sortFeld } }];
  return schreiben(m);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Bearbeiten (unveränderlich: jede Funktion gibt einen neuen Baum zurück)
// ---------------------------------------------------------------------------------------------------------------------------------

function alleKinder(g: Gruppe): Knoten[] {
  return g.kinder;
}

/** Knoten mit dieser ID ersetzen (`null` = entfernen). Die Wurzel selbst lässt sich nicht entfernen, nur ersetzen. */
export function ersetze(g: Gruppe, id: string, fn: (k: Knoten) => Knoten | null): Gruppe {
  if (g.id === id) {
    const neu = fn(g);
    return neu && neu.typ === "gruppe" ? neu : g;
  }
  const kinder: Knoten[] = [];
  for (const k of alleKinder(g)) {
    if (k.id === id) {
      const neu = fn(k);
      if (neu) kinder.push(neu);
    } else if (k.typ === "gruppe") kinder.push(ersetze(k, id, fn));
    else if (k.typ === "verwandt") kinder.push({ ...k, innen: ersetze(k.innen, id, fn) });
    else kinder.push(k);
  }
  return { ...g, kinder };
}

/** Knoten ans Ende der Gruppe mit dieser ID hängen (die Gruppe darf auch das Innere eines verwandten Objekts sein). */
export function fuegeEin(g: Gruppe, gruppenId: string, knoten: Knoten): Gruppe {
  if (g.id === gruppenId) return { ...g, kinder: [...g.kinder, knoten] };
  return {
    ...g,
    kinder: g.kinder.map((k) => (k.typ === "gruppe" ? fuegeEin(k, gruppenId, knoten) : k.typ === "verwandt" ? { ...k, innen: fuegeEin(k.innen, gruppenId, knoten) } : k)),
  };
}

export function finde(g: Gruppe, id: string): Knoten | null {
  if (g.id === id) return g;
  for (const k of g.kinder) {
    if (k.id === id) return k;
    if (k.typ === "gruppe") {
      const r = finde(k, id);
      if (r) return r;
    } else if (k.typ === "verwandt") {
      const r = finde(k.innen, id);
      if (r) return r;
    }
  }
  return null;
}

/** Alle Bedingungen (Blätter) in Reihenfolge; ein verwandtes Objekt zählt als eine Bedingung plus seine inneren. */
export function blaetter(g: Gruppe): (FeldKnoten | VolltextKnoten | VerwandtKnoten | RohKnoten)[] {
  const aus: (FeldKnoten | VolltextKnoten | VerwandtKnoten | RohKnoten)[] = [];
  for (const k of g.kinder) {
    if (k.typ === "gruppe") aus.push(...blaetter(k));
    else if (k.typ === "verwandt") {
      aus.push(k, ...blaetter(k.innen));
    } else aus.push(k);
  }
  return aus;
}

export function zaehle(g: Gruppe): number {
  return blaetter(g).length;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Katalog nachschlagen
// ---------------------------------------------------------------------------------------------------------------------------------

export function feldSchluessel(ref: FilterFeldRef): string {
  return ref.type === "custom_field" ? `c:${ref.custom_field_id}` : `r:${ref.object_type}:${ref.field_name}`;
}

export interface KatalogIndex {
  objekt: string;
  haupt: Map<string, FilterKatalogFeld>;
  verwandt: Record<string, Map<string, FilterKatalogFeld>>;
}

export function katalogIndex(kat: FilterKatalog | null): KatalogIndex | null {
  if (!kat) return null;
  const fuellen = (kategorien: FilterKatalog["kategorien"]) => {
    const m = new Map<string, FilterKatalogFeld>();
    for (const k of kategorien) for (const f of k.felder) if (f.feld) m.set(feldSchluessel(f.feld), f);
    return m;
  };
  const verwandt: Record<string, Map<string, FilterKatalogFeld>> = {};
  for (const v of kat.verwandt) verwandt[v.objekt] = fuellen(v.kategorien);
  return { objekt: kat.objekt, haupt: fuellen(kat.kategorien), verwandt };
}

/** Feld im Katalog: `ebene` = Objektart, auf die sich die Bedingung bezieht (die Hauptart oder die eines verwandten Objekts). */
export function feldInfo(index: KatalogIndex | null, ebene: string, ref: FilterFeldRef): FilterKatalogFeld | undefined {
  if (!index) return undefined;
  const tabelle = ebene === index.objekt ? index.haupt : index.verwandt[ebene];
  return tabelle?.get(feldSchluessel(ref)) ?? (ebene === index.objekt ? undefined : index.haupt.get(feldSchluessel(ref)));
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Neue Bedingungen
// ---------------------------------------------------------------------------------------------------------------------------------

export function neueBedingung(f: FilterKatalogFeld): FeldKnoten | VolltextKnoten {
  if (f.art === "volltext" || !f.feld) return { typ: "volltext", id: neueId(), negate: false, modus: "beginning_of_words", wert: "" };
  const art = f.bedingungen[0];
  let bedingung: Bedingung;
  switch (art) {
    case "verweis":
      bedingung = { art: "verweis", typ: f.referenz ?? "user_or_group", ids: [] };
      break;
    case "begriff":
      bedingung = { art: "begriff", werte: [] };
      break;
    case "zeitraum":
      bedingung = { art: "zeitraum", ab: null, vor: null };
      break;
    case "zahl":
      bedingung = { art: "zahl", gt: null, gte: null, lt: null, lte: null };
      break;
    case "dauer":
      bedingung = { art: "dauer", gt: null, gte: null, lt: null, lte: null };
      break;
    case "existiert":
      bedingung = { art: "existiert" };
      break;
    default:
      bedingung = { art: "text", modus: "beginning_of_words", wert: "" };
  }
  return { typ: "feld", id: neueId(), feld: f.feld, negate: false, bedingung };
}

export function neuesVerwandtes(dieses: string, verwandt: string): VerwandtKnoten {
  return { typ: "verwandt", id: neueId(), dieses, verwandt, negate: false, innen: leereGruppe() };
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Operatoren
// ---------------------------------------------------------------------------------------------------------------------------------

export type ZahlOperator = "kleiner" | "hoechstens" | "genau" | "mindestens" | "groesser" | "zwischen" | "frei";
export const ZAHL_OPERATOREN: { wert: ZahlOperator; label: string }[] = [
  { wert: "kleiner", label: "kleiner als" },
  { wert: "hoechstens", label: "höchstens" },
  { wert: "genau", label: "genau" },
  { wert: "mindestens", label: "mindestens" },
  { wert: "groesser", label: "größer als" },
  { wert: "zwischen", label: "zwischen" },
];

export function zahlOperator(g: Grenzen): ZahlOperator {
  const { gt, gte, lt, lte } = g;
  if (gt === null && gte === null && lt === null && lte === null) return "mindestens";
  if (gt === null && gte === null && lte === null && lt !== null) return "kleiner";
  if (gt === null && gte === null && lt === null && lte !== null) return "hoechstens";
  if (gt === null && lt === null && gte !== null && lte !== null) return gte === lte ? "genau" : "zwischen";
  if (gt === null && lt === null && lte === null && gte !== null) return "mindestens";
  if (gte === null && lt === null && lte === null && gt !== null) return "groesser";
  return "frei";
}

export function zahlMitOperator(g: Grenzen, op: ZahlOperator): Grenzen {
  const wert = g.gte ?? g.gt ?? g.lt ?? g.lte;
  const ohne: Grenzen = { gt: null, gte: null, lt: null, lte: null };
  switch (op) {
    case "kleiner":
      return { ...ohne, lt: wert };
    case "hoechstens":
      return { ...ohne, lte: wert };
    case "genau":
      return { ...ohne, gte: wert, lte: wert };
    case "mindestens":
      return { ...ohne, gte: wert };
    case "groesser":
      return { ...ohne, gt: wert };
    case "zwischen":
      return { ...ohne, gte: wert, lte: g.lte !== null && g.lte !== wert ? g.lte : null };
    default:
      return g;
  }
}

export type ZeitOperator = "ab" | "bis" | "zwischen";
export const ZEIT_OPERATOREN: { wert: ZeitOperator; label: string }[] = [
  { wert: "ab", label: "ab (einschließlich)" },
  { wert: "bis", label: "bis (ausschließlich)" },
  { wert: "zwischen", label: "zwischen" },
];

export function zeitOperator(b: { ab: Moment | null; vor: Moment | null }): ZeitOperator {
  if (b.ab && b.vor) return "zwischen";
  if (b.vor && !b.ab) return "bis";
  return "ab";
}

export const TEXT_OPERATOREN: { wert: TextModus; label: string }[] = [
  { wert: "beginning_of_words", label: "enthält" },
  { wert: "full_words", label: "enthält ganze Wörter" },
  { wert: "phrase", label: "enthält die Wortfolge" },
];

// ---------------------------------------------------------------------------------------------------------------------------------
// Schnellwahl für Zeiträume
// ---------------------------------------------------------------------------------------------------------------------------------

const periode = (bereich: string, which: "start" | "end"): Moment => ({ art: "periode", bereich, which });
const versatz = (menge: number, einheit: Einheit, richtung: "past" | "future", tagesgrenze: Tagesgrenze = "start"): Moment => ({ art: "versatz", menge, einheit, richtung, tagesgrenze });

export const ZEIT_SCHNELLWAHL: { id: string; label: string; ab: Moment | null; vor: Moment | null }[] = [
  { id: "heute", label: "Heute", ab: periode("today", "start"), vor: periode("today", "end") },
  { id: "gestern", label: "Gestern", ab: periode("yesterday", "start"), vor: periode("yesterday", "end") },
  { id: "morgen", label: "Morgen", ab: periode("tomorrow", "start"), vor: periode("tomorrow", "end") },
  { id: "diese_woche", label: "Diese Woche", ab: periode("this_week", "start"), vor: periode("this_week", "end") },
  { id: "diesen_monat", label: "Dieser Monat", ab: periode("this_month", "start"), vor: periode("this_month", "end") },
  { id: "letzte_7", label: "Letzte 7 Tage", ab: versatz(7, "days", "past"), vor: null },
  { id: "letzte_30", label: "Letzte 30 Tage", ab: versatz(30, "days", "past"), vor: null },
  { id: "vor_heute", label: "Vor heute", ab: null, vor: periode("today", "start") },
  { id: "ab_heute", label: "Ab heute", ab: periode("today", "start"), vor: null },
  { id: "naechste_7", label: "Nächste 7 Tage", ab: periode("today", "start"), vor: versatz(7, "days", "future") },
];

export const PERIODEN: { wert: string; label: string; genitiv: string }[] = [
  { wert: "today", label: "heute", genitiv: "heute" },
  { wert: "yesterday", label: "gestern", genitiv: "gestern" },
  { wert: "tomorrow", label: "morgen", genitiv: "morgen" },
  { wert: "this_week", label: "diese Woche", genitiv: "dieser Woche" },
  { wert: "last_week", label: "letzte Woche", genitiv: "letzter Woche" },
  { wert: "next_week", label: "nächste Woche", genitiv: "nächster Woche" },
  { wert: "this_month", label: "dieser Monat", genitiv: "dieses Monats" },
  { wert: "last_month", label: "letzter Monat", genitiv: "letzten Monats" },
  { wert: "next_month", label: "nächster Monat", genitiv: "nächsten Monats" },
  { wert: "this_quarter", label: "dieses Quartal", genitiv: "dieses Quartals" },
  { wert: "last_quarter", label: "letztes Quartal", genitiv: "letzten Quartals" },
  { wert: "next_quarter", label: "nächstes Quartal", genitiv: "nächsten Quartals" },
  { wert: "this_year", label: "dieses Jahr", genitiv: "dieses Jahres" },
  { wert: "last_year", label: "letztes Jahr", genitiv: "letzten Jahres" },
  { wert: "next_year", label: "nächstes Jahr", genitiv: "nächsten Jahres" },
];

export const EINHEIT_AUSWAHL: { wert: Einheit; label: string }[] = [
  { wert: "minutes", label: "Minuten" },
  { wert: "hours", label: "Stunden" },
  { wert: "days", label: "Tage" },
  { wert: "weeks", label: "Wochen" },
  { wert: "months", label: "Monate" },
  { wert: "years", label: "Jahre" },
];

export const EINHEIT_TEXTE: Record<Einheit, { eins: string; viele: string }> = {
  minutes: { eins: "Minute", viele: "Minuten" },
  hours: { eins: "Stunde", viele: "Stunden" },
  days: { eins: "Tag", viele: "Tagen" },
  weeks: { eins: "Woche", viele: "Wochen" },
  months: { eins: "Monat", viele: "Monaten" },
  years: { eins: "Jahr", viele: "Jahren" },
};

/** Gleiche Zeitpunkte? (für die Schnellwahl und das Erkennen von „heute“ & Co.) */
export function gleicherMoment(a: Moment | null, b: Moment | null): boolean {
  return kanonisch(a) === kanonisch(b);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Texte
// ---------------------------------------------------------------------------------------------------------------------------------

export interface PillenText {
  feld: string;
  /** leiser Operator vor dem Wert („nicht“, „ab“, „kleiner als“) */
  op: string;
  /** Wert, fett */
  wert: string;
}

export interface TextKontext {
  /** Bezeichnung des Feldes (aus dem Katalog; sonst der Schlüssel) */
  feldName: (ref: FilterFeldRef, ebene: string) => string;
  /** Anzeigename eines Wertes (Status, Nutzer, Auswahl) */
  optionName: (ref: FilterFeldRef, ebene: string, wert: string) => string;
}

function datumDe(tag: string): string {
  const [j, m, t] = tag.split("-");
  return `${t}.${m}.${j}`;
}

function menge(n: number): string {
  return n.toLocaleString("de-DE");
}

/** Dauer in Sekunden als kurzer Text („20 Sek.“, „1 Min. 30 Sek.“, „2 Std.“). */
export function dauerText(sek: number): string {
  if (sek === 0) return "0 Sek.";
  const t = Math.floor(sek / 86400);
  const h = Math.floor((sek % 86400) / 3600);
  const m = Math.floor((sek % 3600) / 60);
  const s = sek % 60;
  const teile = [t ? `${menge(t)} ${t === 1 ? "Tag" : "Tage"}` : "", h ? `${h} Std.` : "", m ? `${m} Min.` : "", s ? `${menge(s)} Sek.` : ""].filter(Boolean);
  return teile.join(" ");
}

export function momentText(m: Moment): string {
  switch (m.art) {
    case "jetzt":
      return "jetzt";
    case "versatz": {
      const e = EINHEIT_TEXTE[m.einheit];
      // ein negativer Wert dreht die Richtung um (Close speichert „morgen“ als „vor −1 Tagen“)
      const vergangen = (m.richtung === "past") === (m.menge >= 0);
      const n = Math.abs(m.menge);
      const grenze = m.einheit === "days" || m.einheit === "weeks" || m.einheit === "months" || m.einheit === "years";
      if (n === 0 && grenze) return "jetzt";
      const text = `${vergangen ? "vor" : "in"} ${menge(n)} ${n === 1 ? e.eins : e.viele}`;
      return `${text}${grenze && m.tagesgrenze === "start" ? " (0 Uhr)" : grenze && m.tagesgrenze === "end" ? " (Tagesende)" : ""}`;
    }
    case "periode": {
      const p = PERIODEN.find((x) => x.wert === m.bereich);
      const name = p?.genitiv ?? m.bereich;
      return `${m.which === "start" ? "Beginn" : "Ende"} ${name}`;
    }
    case "datum":
      return m.which === "end" ? `Ende ${datumDe(m.tag)}` : datumDe(m.tag);
    default:
      return "anderer Zeitpunkt";
  }
}

function zeitraumText(ab: Moment | null, vor: Moment | null): { op: string; wert: string } {
  if (ab && vor) {
    if (ab.art === "periode" && vor.art === "periode" && ab.bereich === vor.bereich && ab.which === "start" && vor.which === "end") {
      const p = PERIODEN.find((x) => x.wert === ab.bereich);
      return { op: "", wert: p?.label ?? ab.bereich };
    }
    if (ab.art === "datum" && vor.art === "datum" && ab.tag === vor.tag && ab.which === "start" && vor.which === "end") return { op: "am", wert: datumDe(ab.tag) };
    return { op: "", wert: `ab ${momentText(ab)} · bis ${momentText(vor)}` };
  }
  if (ab) return { op: "ab", wert: momentText(ab) };
  if (vor) return { op: "bis", wert: momentText(vor) };
  return { op: "", wert: "…" };
}

function grenzenText(g: Grenzen, format: (n: number) => string): { op: string; wert: string } {
  const op = zahlOperator(g);
  switch (op) {
    case "kleiner":
      return { op: "kleiner als", wert: format(g.lt as number) };
    case "hoechstens":
      return { op: "höchstens", wert: format(g.lte as number) };
    case "genau":
      return { op: "genau", wert: format(g.gte as number) };
    case "mindestens":
      return g.gte === null ? { op: "", wert: "…" } : { op: "mindestens", wert: format(g.gte) };
    case "groesser":
      return { op: "größer als", wert: format(g.gt as number) };
    case "zwischen":
      return { op: "zwischen", wert: g.lte === null ? format(g.gte as number) : `${format(g.gte as number)} und ${format(g.lte)}` };
    default: {
      const teile = [g.gt !== null ? `größer als ${format(g.gt)}` : "", g.gte !== null ? `mindestens ${format(g.gte)}` : "", g.lt !== null ? `kleiner als ${format(g.lt)}` : "", g.lte !== null ? `höchstens ${format(g.lte)}` : ""];
      return { op: "", wert: teile.filter(Boolean).join(" und ") };
    }
  }
}

const TEXT_VERB: Record<TextModus, string> = { beginning_of_words: "enthält", full_words: "enthält ganze Wörter", phrase: "enthält die Wortfolge" };

/** Text einer Bedingung für die Pille: Feld, leiser Operator, fetter Wert. */
export function pillenText(k: FeldKnoten | VolltextKnoten, ebene: string, ctx: TextKontext): PillenText {
  const nicht = (s: string) => (k.negate ? (s ? `nicht ${s}` : "nicht") : s);
  if (k.typ === "volltext") return { feld: "Beliebiger Text", op: nicht(TEXT_VERB[k.modus]), wert: k.wert ? `„${k.wert}“` : "…" };
  const feld = ctx.feldName(k.feld, ebene);
  const b = k.bedingung;
  switch (b.art) {
    case "verweis":
    case "begriff": {
      const werte = b.art === "verweis" ? b.ids : b.werte;
      const namen = werte.map((w) => ctx.optionName(k.feld, ebene, w));
      return { feld, op: nicht(""), wert: namen.length ? namen.join(" oder ") : "…" };
    }
    case "existiert":
      return { feld, op: "", wert: k.negate ? "ist leer" : "ist gesetzt" };
    case "zeitraum": {
      const z = zeitraumText(b.ab, b.vor);
      return { feld, op: nicht(z.op), wert: z.wert };
    }
    case "zahl": {
      const z = grenzenText(b, menge);
      return { feld, op: nicht(z.op), wert: z.wert };
    }
    case "dauer": {
      const z = grenzenText(b, dauerText);
      return { feld, op: nicht(z.op), wert: z.wert };
    }
    case "text":
      return { feld, op: nicht(TEXT_VERB[b.modus]), wert: b.wert ? `„${b.wert}“` : "…" };
    default:
      return { feld, op: "", wert: "andere Bedingung" };
  }
}

export type GruppenArt = "alle" | "eine" | "nicht_alle" | "keine";
export const GRUPPEN_ARTEN: { wert: GruppenArt; label: string; kurz: string }[] = [
  { wert: "alle", label: "Alle Bedingungen gelten", kurz: "alle von" },
  { wert: "eine", label: "Mindestens eine gilt", kurz: "eine von" },
  { wert: "nicht_alle", label: "Nicht alle gelten", kurz: "nicht alle von" },
  { wert: "keine", label: "Keine gilt", kurz: "keine von" },
];

export function gruppenArt(g: Gruppe): GruppenArt {
  return g.und ? (g.negate ? "nicht_alle" : "alle") : g.negate ? "keine" : "eine";
}

export function mitGruppenArt(g: Gruppe, art: GruppenArt): Gruppe {
  return { ...g, und: art === "alle" || art === "nicht_alle", negate: art === "nicht_alle" || art === "keine" };
}

/** Verknüpfungswort zwischen den Geschwistern einer Gruppe („und“ bei und-Gruppen, „oder“ bei oder-Gruppen). */
export function verknuepfer(g: Gruppe): string {
  return g.und ? "und" : "oder";
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Sortierung (Hilfen für die Bedienleiste 5B: Sortierung eines Entwurfs steckt in `s_query.sort`)
// ---------------------------------------------------------------------------------------------------------------------------------

const SORT_NAMEN: Record<string, string> = { display_name: "display_name", name: "display_name", status_label: "status_label", date_created: "date_created", date_updated: "date_updated" };

/** Eintrag für `s_query.sort` aus einem Spaltenschlüssel der Listen (`display_name`, `custom.cf_…`, `num_calls` …); `ab` = absteigend. */
export function sortEintrag(key: string, ab: boolean, objekt = "lead"): { direction: "asc" | "desc"; field: FilterFeldRef } | null {
  const roh = key.replace(/^custom\./, "");
  const field: FilterFeldRef | null = roh.startsWith("cf_") ? { type: "custom_field", custom_field_id: roh } : /^[a-z][a-z0-9_]*$/.test(key) ? { type: "regular_field", object_type: objekt, field_name: SORT_NAMEN[key] ?? key } : null;
  return field ? { direction: ab ? "desc" : "asc", field } : null;
}

/** Erster Sortiereintrag einer Abfrage als Spaltenschlüssel (`ab` = absteigend), `null` ohne Sortierung. */
export function sortSchluessel(sort: unknown[] | null | undefined): { key: string; ab: boolean } | null {
  const e = sort?.[0];
  if (!istObj(e)) return null;
  const f = feldRefLesen(e.field);
  if (!f) return null;
  return { key: f.type === "custom_field" ? `custom.${f.custom_field_id}` : f.field_name, ab: e.direction === "desc" };
}

/**
 * Lupe an einem Lead-Feld (linke Spalte, wie Close): Abfrage „alle Leads, deren Feld diesen Wert hat“.
 * Auswahl/Nutzer/Kontakt: genauer Begriff; Zahl: genau diese Zahl; Text: als Wortfolge. Datum: null (dort keine Lupe).
 */
export function feldSuchAbfrage(feldId: string, typ: string, wert: unknown): SQuery | null {
  const werte = (Array.isArray(wert) ? wert : [wert]).filter((w) => w !== null && w !== undefined && String(w).trim() !== "").map(String);
  if (!werte.length) return null;
  let bedingung: Bedingung;
  if (typ === "choices" || typ === "user" || typ === "contact") bedingung = { art: "begriff", werte };
  else if (typ === "number" && Number.isFinite(Number(werte[0]))) {
    const n = Number(werte[0]);
    bedingung = { art: "zahl", gt: null, gte: n, lt: null, lte: n };
  } else if (typ === "text" || typ === "textarea" || typ === "richtextarea") {
    const roh = werte[0].replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200);
    if (!roh) return null;
    bedingung = { art: "text", modus: "phrase", wert: roh };
  } else return null;
  const s = standardAbfrage("lead") as SQuery & { query: { queries?: unknown[] } };
  const bedingungen = Array.isArray(s.query.queries) ? s.query.queries : [];
  return {
    ...s,
    query: {
      ...s.query,
      queries: [...bedingungen, { type: "field_condition", field: { type: "custom_field", custom_field_id: feldId }, condition: bedingungSchreiben(bedingung), negate: false }],
    },
  } as SQuery;
}
